import React, { useMemo, useReducer, useEffect, useCallback, useRef } from "react";
import type { FC } from "react";

import { Box, HStack, Button } from "native-base";
import { ScrollView } from "react-native";
import { useQueryLoader } from "react-relay";

import SideBar from "pianofunclub-crm/components/Drawers/SideBar";
import BlacklistedDates, {
  LOAD_X_BLACKLISTED_DATES,
} from "pianofunclub-crm/components/Schools/BlacklistedDates";
import type { BlacklistedDatesReducerValues } from "pianofunclub-crm/components/Schools/BlacklistedDates";
import YearTermDates from "pianofunclub-crm/components/Schools/YearTermDates";
import type { YearTermDatesReducerValues } from "pianofunclub-crm/components/Schools/YearTermDates";
import type {
  AccountsStackNavigatorProps,
  AccountsStackRouteProps,
} from "pianofunclub-crm/navigation/AccountsNavigator";
import type {
  InvoicingStackNavigatorProps,
  InvoicingStackRouteProps,
} from "pianofunclub-crm/navigation/InvoicingNavigator";
import type {
  PayrollStackNavigatorProps,
  PayrollStackRouteProps,
} from "pianofunclub-crm/navigation/PayrollNavigator";
import type {
  RegistersStackNavigatorProps,
  RegistersStackRouteProps,
} from "pianofunclub-crm/navigation/RegistersNavigator";
import type {
  SchoolsStackNavigatorProps,
  SchoolsStackRouteProps,
} from "pianofunclub-crm/navigation/SchoolsNavigator";
import type {
  WaitingListStackNavigatorProps,
  WaitingListStackRouteProps,
} from "pianofunclub-crm/navigation/WaitingListNavigator";

import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import { RestartIcon } from "pianofunclub-shared/components/Other/Icons";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type { LoadBlacklistedDatesQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadBlacklistedDatesQuery.graphql";
import type { LoadTermDatesQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadTermDatesQuery.graphql";
import { load_blacklisted_dates } from "pianofunclub-shared/relay/graphql/schools/LoadBlacklistedDates";
import { load_term_dates } from "pianofunclub-shared/relay/graphql/schools/LoadTermDates";
import { customColors } from "pianofunclub-shared/theme";
import { getDefaultStartingYearAndStartingYearsForSelect } from "pianofunclub-shared/utils/extractors";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { State } from "pianofunclub-shared/utils/reducers";

export type NavigationProps = AccountsStackNavigatorProps<"DefaultTermDates"> &
  InvoicingStackNavigatorProps<"DefaultTermDates"> &
  SchoolsStackNavigatorProps<"DefaultTermDates"> &
  RegistersStackNavigatorProps<"DefaultTermDates"> &
  WaitingListStackNavigatorProps<"DefaultTermDates"> &
  PayrollStackNavigatorProps<"DefaultTermDates">;

export type RouteProps = AccountsStackRouteProps<"DefaultTermDates"> &
  InvoicingStackRouteProps<"DefaultTermDates"> &
  SchoolsStackRouteProps<"DefaultTermDates"> &
  RegistersStackRouteProps<"DefaultTermDates"> &
  WaitingListStackRouteProps<"DefaultTermDates"> &
  PayrollStackRouteProps<"DefaultTermDates">;

interface Props {
  navigation: NavigationProps;
  route: RouteProps;
}

interface ReducerValues extends YearTermDatesReducerValues, BlacklistedDatesReducerValues {}

const SIDE_BAR_WIDTH = 12;

const DeafultTermDatesScreen: FC<Props> = (props) => {
  const { navigation, route } = props;

  const { user } = useAuth();

  const scrollViewRef = useRef<ScrollView>(null);

  const [defaultStartingYear, startingYears] = useMemo(
    () => getDefaultStartingYearAndStartingYearsForSelect(),
    []
  );

  const initialState: State<ReducerValues> = useMemo(() => {
    return {
      values: {
        startingYear:
          route.params?.startingYear ??
          user?.profile?.profileGroup?.currentStartingYear ??
          defaultStartingYear,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadTermDatesQueryReference, loadTermDatesQuery] =
    useQueryLoader<LoadTermDatesQuery>(load_term_dates);

  const [loadBlacklistedDatesQueryReference, loadBlacklistedDatesQuery] =
    useQueryLoader<LoadBlacklistedDatesQuery>(load_blacklisted_dates);

  useEffect(() => {
    loadTermDatesQuery(
      {
        skipSchoolTermDates: true,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "store-or-network" }
    );

    loadBlacklistedDatesQuery(
      {
        first: LOAD_X_BLACKLISTED_DATES,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "store-or-network" }
    );
  }, [loadBlacklistedDatesQuery, loadTermDatesQuery, state.values.startingYear]);

  useEffect(() => {
    if (route.params?.scrollToBottom) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd();
      }, 200);
    }
  }, [route]);

  const termDatesInputKey = useRef(0);

  const refreshHandler = useCallback(() => {
    loadTermDatesQuery(
      {
        skipSchoolTermDates: true,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "network-only" }
    );
    termDatesInputKey.current += 1;

    loadBlacklistedDatesQuery(
      {
        first: LOAD_X_BLACKLISTED_DATES,
        startingYear: state.values.startingYear,
      },
      { fetchPolicy: "network-only" }
    );
  }, [loadBlacklistedDatesQuery, loadTermDatesQuery, state.values.startingYear]);

  const renderHeader = useMemo(() => {
    return (
      <HStack space="4">
        <Select
          borderRadius="2xl"
          fontSize="md"
          onValueChange={(itemValue) => {
            dispatchState({
              input: "startingYear",
              value: parseInt(itemValue),
            });
            navigation.setParams({
              startingYear: parseInt(itemValue),
            });
          }}
          placeholder="Select year"
          selectedValue={String(state.values.startingYear)}
          width="40"
        >
          {startingYears.map((item) => {
            return (
              <Select.Item
                key={item.value}
                actionSheetLabel={item.label}
                value={String(item.value)}
              />
            );
          })}
        </Select>
        <Button
          bg="surface.400"
          colorScheme="surface"
          leftIcon={<RestartIcon size="5" />}
          onPress={() => {
            if (state.values.termDatesHaveBeenModified) {
              dispatchState({
                input: "leaveAlertAction",
                value: () => refreshHandler(),
              });
            } else {
              refreshHandler();
            }
          }}
          p="3"
        />
      </HStack>
    );
  }, [
    navigation,
    refreshHandler,
    startingYears,
    state.values.startingYear,
    state.values.termDatesHaveBeenModified,
  ]);

  return (
    <Box flex={1}>
      <SideBar isCollapsed navigation={navigation} width={SIDE_BAR_WIDTH} />
      <ScrollView
        ref={scrollViewRef}
        style={{
          backgroundColor: customColors.surface[100],
          flex: 1,
          marginLeft: SIDE_BAR_WIDTH * 4,
          marginTop: 70,
          paddingHorizontal: 24,
          paddingVertical: 24,
        }}
      >
        <Box>
          {renderHeader}
          <YearTermDates
            key={termDatesInputKey.current}
            dispatchScreenState={dispatchState}
            loadTermDatesQueryReference={loadTermDatesQueryReference}
            navigation={navigation}
            screenState={state}
          />
        </Box>
        <Box mt="70">
          <BlacklistedDates
            loadBlacklistedDatesQueryReference={loadBlacklistedDatesQueryReference}
            navigation={navigation}
            screenState={state}
            scrollViewRef={scrollViewRef}
            sideBarWidth={SIDE_BAR_WIDTH}
          />
        </Box>
      </ScrollView>
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Default Term Dates",
};

export default DeafultTermDatesScreen;
